import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getMyUser } from '../services/usuarios.service';
import { loginConCidi } from '../services/auth.service';
import apiClient from '../services/apiClient';
import config from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cidiError, setCidiError] = useState(null);

    const logout = useCallback(async (forced = false) => {
        try {
            const URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
            // Le decimos al backend que borre la cookie JWT
            await apiClient(`${URL}/auth/logout`, { method: 'POST' });
        } catch (error) {
            console.error("Error en logout:", error);
        } finally {
            localStorage.removeItem('lastActivityTimestamp');
            sessionStorage.clear();

            // Intentar borrar la cookie de CiDi del navegador
            document.cookie = "CiDi=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            document.cookie = "CiDi=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.cba.gov.ar";
            document.cookie = "CiDi=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.test.cba.gov.ar";

            setUser(null);
            if (forced) {
                // Redirigir al endpoint de Logout de CiDi para destruir su sesión del servidor.
                // Esto previene la re-autenticación automática al volver a entrar.
                window.location.href = config.cidiLogoutUrl;
            }
        }
    }, []);

    /**
     * Intenta autenticar al usuario vía CiDi usando un hashCookie.
     * Retorna true si el login fue exitoso, false si no.
     */
    const loginCidi = useCallback(async (hashCookie) => {
        try {
            setCidiError(null);
            await loginConCidi(hashCookie); // La cookie JWT se setea sola desde el backend

            // La cookie "CiDi" se mantiene en el navegador para preservar la sesión SSO en otras aplicaciones (.cba.gov.ar)

            return true;
        } catch (error) {
            console.error("Error en login CiDi:", error.message);
            setCidiError(error.message);
            return false;
        }
    }, []);

    const checkAuth = useCallback(async () => {
        const cidiCookie = document.cookie
            .split('; ')
            .find(row => row.startsWith('CiDi='));

        if (cidiCookie) {
            const hashCookie = cidiCookie.split('=')[1];
            // Intentar login con CiDi
            const success = await loginCidi(hashCookie);
            if (success) {
                // Obtener datos del usuario con la nueva cookie JWT
                try {
                    const res = await getMyUser();
                    if (res) {
                        setUser(res);
                    }
                } catch (err) {
                    console.error("Error al obtener usuario tras CiDi login", err);
                }
            }
            setLoading(false);
            return;
        }

        try {
            const res = await getMyUser();
            if (res) {
                setUser(res);
            } else {
                setUser(null);
            }
        } catch (error) {
            // Si getMyUser falla (ej. 401 Unauthorized), setUser a null
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [loginCidi]);



    useEffect(() => {
        checkAuth();

        // Sync logout across tabs
        const handleStorageChange = (e) => {
            if (e.key === 'jwt' && !e.newValue) {
                logout();
            }
            if (e.key === 'jwt' && e.newValue) {
                checkAuth();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [checkAuth, logout]);

    return (
        <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, cidiError, setCidiError }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
